import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import MessageTemplateService from "../services/MessageTemplateService";
import appConfig from "../shared/js/app-config";

export default function TemplateLibraryPanel({ onAfterClone }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cloningId, setCloningId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [myTemplates, setMyTemplates] = useState([]);

  const serviceId = appConfig.getServiceId();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [defaults, mine] = await Promise.all([
        MessageTemplateService.getDefaultTemplates(),
        MessageTemplateService.getAllTemplates(),
      ]);
      setRows(defaults);
      setMyTemplates(mine);
    } catch {
      setRows([]);
      setMyTemplates([]);
      setSnackbar({ open: true, message: "Failed to load template library", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isAlreadyCloned = (defaultId) =>
    (myTemplates || []).some((t) => t.getSourceDefaultTemplateId?.() === defaultId);

  const handleClone = async (row) => {
    if (isAlreadyCloned(row.id)) return;
    setCloningId(row.id);
    try {
      await MessageTemplateService.cloneDefaultTemplate(row.id, serviceId);
      setSnackbar({
        open: true,
        message: "Template added to your account. Edit if needed, then sync to Meta for approval.",
        severity: "success",
      });
      await load();
      onAfterClone?.();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e?.response?.data?.message || e?.message || "Failed to add template",
        severity: "error",
      });
    } finally {
      setCloningId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 2,
          p: 5,
          textAlign: "center",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          No templates in the library yet
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          When your platform admin publishes default templates, they will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ready-made templates from your platform. Add to your account, customize, then submit to Meta for approval.
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell sx={{ fontWeight: 700 }}>Template</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Language</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const already = isAlreadyCloned(row.id);
              return (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography fontWeight={700}>{row.template_name}</Typography>
                    {row.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {row.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{row.template_type || "—"}</TableCell>
                  <TableCell>{row.template_language || "—"}</TableCell>
                  <TableCell>
                    {already ? (
                      <Chip size="small" color="success" label="Added" />
                    ) : (
                      <Chip size="small" variant="outlined" label="Not added" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={already || cloningId === row.id}
                        onClick={() => handleClone(row)}
                      >
                        {already
                          ? "Already in your templates"
                          : cloningId === row.id
                            ? "Adding..."
                            : "Use this template"}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
